import {Box, Drawer, MenuItem, Paper, Typography} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import {alpha, css, styled} from '@mui/material/styles';
import Icon from 'presentation/icons';
import {StyledBoxProps, StyledMenuProps} from './types';

const isHapTheme = (themeName?: string, isHomePage?: boolean) => themeName === 'hap' || !isHomePage;

const ProfileContainer = styled(IconButton)`
	border-radius: 48px;
	padding: 0 4px 0 0;

	&:is(&, :active, :focus) {
		background-color: ${({theme}) => alpha(theme.palette.common.white, 0.16)};
	}
`;
const ProfileContainerLegacy = styled(IconButton)<StyledMenuProps>`
	border-radius: 48px;
	padding: 8px;

	&:is(&, :active, :focus) {
		background-color: ${({theme, $isHomePage}) =>
			isHapTheme(theme.name, $isHomePage)
				? alpha(theme.palette.common.white, 0.18)
				: alpha(theme.palette.common.white, 0.56)};
	}
`;

const GradientAvatarWrapper = styled(Box)`
	background: conic-gradient(from 90deg at 50% 50%, #ff222b 0deg, #ffcc23 180deg, #ff222b 360deg);
	border-radius: 50%;

	display: flex;
	width: 42px;
	height: 42px;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const ProfileIcon = styled(Avatar)`
	background-color: ${({theme}) => theme.colors.light.primary['170Percent']};
	width: 38px;
	height: 38px;
`;

const ProfileIconLegacy = styled(Avatar)<StyledMenuProps>`
	${({theme, $isHomePage}) => {
		const hap = isHapTheme(theme.name, $isHomePage);

		return css`
			background-color: ${hap ? theme.palette.common.white : theme.palette.other?.planColor};

			color: ${hap ? theme.palette.primary.main : alpha(theme.palette.common.white, 0.56)};
		`;
	}}

	${({theme}) => theme.breakpoints.down('sm')} {
		width: 32px;
		height: 32px;
	}
`;

const StyledMenuItemLegacy = styled(MenuItem)`
	gap: 8px;
	padding: 16px;
	color: ${({theme}) => theme.palette.text.secondary};
`;

const ArrowIcon = styled(Icon)<StyledMenuProps>`
	color: ${({theme, $isHomePage}) =>
		isHapTheme(theme.name, $isHomePage)
			? alpha(theme.palette.common.white, 0.56)
			: theme.palette.other?.planColor};
`;

const StyledBeneficiaryName = styled(Typography)`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	font-size: 0.75rem;
	font-weight: 700;
	line-height: 1.66;
	letter-spacing: 0.4px;
`;

const StyledBeneficiaryNameLegacy = styled(Typography)<StyledMenuProps>`
	font-weight: 600;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: ${({theme, $isHomePage}) =>
		isHapTheme(theme.name, $isHomePage)
			? theme.palette.common.white
			: theme.palette.common.black};

	${({theme}) => theme.breakpoints.down('sm')} {
		font-size: 0.75rem;
		font-weight: 700;
		line-height: 1.66;
		letter-spacing: 0.4px;
	}
`;

const StyledPlanName = styled(Typography)<StyledMenuProps>`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	color: ${({theme, $isHomePage}) =>
		isHapTheme(theme.name, $isHomePage)
			? theme.palette.common.white
			: theme.palette.common.black};

	${({theme}) => theme.breakpoints.down('sm')} {
		font-size: 0.75rem;
		font-weight: 400;
		line-height: 1;
		letter-spacing: 0.15px;
	}
`;

const StyledInitials = styled(Typography)`
	font-size: 1.125rem;
	line-height: 1;
	letter-spacing: 0.14px;
	color: ${({theme}) => theme.palette.common.white};
`;

const StyledInitialsLegacy = styled(Typography)<StyledMenuProps>`
	font-size: 1.25rem;
	line-height: 1;
	letter-spacing: 0.14px;

	color: ${({theme, $isHomePage}) =>
		isHapTheme(theme.name, $isHomePage)
			? theme.palette.primary.main
			: alpha(theme.palette.common.white, 0.56)};
`;

const StyledBox = styled(Box)<StyledBoxProps>`
	max-width: 180px;

	${({theme}) => theme.breakpoints.down(460)} {
		max-width: 108px;
	}

	${({theme}) => theme.breakpoints.down(410)} {
		max-width: 95px;
	}

	${({theme}) => theme.breakpoints.down(375)} {
		max-width: 80px;
	}
`;

const StyledDrawer = styled(Drawer)(({theme}) => ({
	'& > .MuiPaper-root': {
		width: 'calc(100vw - 48px)',
		borderRadius: '12px 0 0 12px',
		padding: 12,
		backgroundColor: theme.colors.gray?.['20Percent'] ?? theme.palette.background.paper,
	},
}));

const StyledPopperPaper = styled(Paper)(({theme}) => ({
	marginTop: 2,
	maxWidth: 312,
	padding: 12,
	borderRadius: 12,
	backgroundColor: theme.colors.gray?.['20Percent'] ?? theme.palette.background.paper,
}));

export {
	ArrowIcon,
	GradientAvatarWrapper,
	ProfileContainer,
	ProfileContainerLegacy,
	ProfileIcon,
	ProfileIconLegacy,
	StyledBeneficiaryName,
	StyledBeneficiaryNameLegacy,
	StyledBox,
	StyledDrawer,
	StyledInitials,
	StyledInitialsLegacy,
	StyledMenuItemLegacy,
	StyledPlanName,
	StyledPopperPaper,
};
